import { useEffect, useState } from "react";
import {
  Button,
  Divider,
  Drawer,
  message,
  Input,
  Space,
  Spin,
  Popconfirm,
  List,
  Image,
  Flex,
  Modal,
  Form,
} from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-number-input/input";

import authCheck from "../../APIs/authCheck";
import publicDataServices from "../../APIs/publicDataServices";
import userServices from "../../APIs/userServices";
import { getProductUrl } from "./helpers";

const CartDrawer = ({ open, onClose, setOpen }) => {
  const [form] = Form.useForm();

  const navigate = useNavigate();

  const [messageApi, contextHolder] = message.useMessage();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");

  const [cartSpecialInstructions, setCartSpecialInstructions] = useState("");

  const [cartItems, setCartItems] = useState(
    JSON.parse(localStorage.getItem("cartItems")) || [],
  );

  const [unavailableOptions, setUnavailableOptions] = useState(new Set());

  const [openGuestInfoModal, setOpenGuestInfoModal] = useState(false);

  useEffect(() => {
    if (open) {
      const stored = JSON.parse(localStorage.getItem("cartItems")) || [];

      setCartItems(stored);
    }
  }, [open]);

  const norm = (s = "") => s.trim().toLowerCase();

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const checkAuth = async () => {
    try {
      const response = await authCheck.authCheck();

      if (response?.status === 200) {
        return true;
      } else {
        // console.log("ask for phone number and email address");

        setOpenGuestInfoModal(true);

        return false;
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setOpenGuestInfoModal(true);
        setIsLoading(false);
        setOpen(true);
      }

      return false;
    }
  };

  const handleRequestQuotation = async () => {
    setIsLoading(true);

    // onClose();

    const checkLoginResult = await checkAuth();

    if (checkLoginResult) {
      setOpenGuestInfoModal(false);

      await handleSendingQuotationInfo();
    }
  };

  const handleSendingQuotationInfo = async () => {
    const wasGuestModalOpen = openGuestInfoModal;

    try {
      setLoadingText("Checking availability...");

      setIsLoading(true);

      setOpenGuestInfoModal(false);

      onClose();

      const formValues = form.getFieldsValue();

      const quotationData = {
        ...formValues,
        cartItems,
        specialInstructions: cartSpecialInstructions,
        isGuest: wasGuestModalOpen,
      };

      // console.log("Sending quotation data:", quotationData);

      const checkAvailability =
        await publicDataServices.checkProductAvailability(quotationData);

      const checkAvailabilityData = JSON.parse(
        checkAvailability.data.data,
        null,
        2,
      );

      // console.log("wasGuestModalOpen:", wasGuestModalOpen);

      await wait(2000);

      if (checkAvailabilityData.allAvailable) {
        setLoadingText("Sending quotation...");

        let response = null;

        if (wasGuestModalOpen) {
          // console.log("Sending quotation as guest:", quotationData);

          response = await publicDataServices.requestQuotation(quotationData);
        } else {
          // console.log("Sending quotation as logged-in user:", quotationData);

          const checkLoginResult = await checkAuth();

          // console.log("checkLoginResult:", checkLoginResult);

          if (checkLoginResult) {
            // console.log(
            //   "User is logged in, sending quotation with userServices:",
            //   quotationData,
            // );

            response = await userServices.requestQuotation(quotationData);
          } else {
            // console.log(
            //   "User is not logged in, sending quotation with publicDataServices:",
            //   quotationData,
            // );

            response = await publicDataServices.requestQuotation(quotationData);
          }
        }

        // console.log("Quotation response:", response);

        if (response?.status !== 200) {
          throw new Error("Send quotation failed. Please try again.");
        } else if (response?.status === 200) {
          messageApi.success(
            "Quotation sent successfully. We will get back to you soon.",
            15,
          );

          // Clear cart items
          localStorage.removeItem("cartItems");
          setCartItems([]);

          setIsLoading(false);
          setLoadingText("");
          setOpen(true);
          setCartSpecialInstructions("");
        }
      } else {
        const unavailableOptionsSet = new Set();

        checkAvailabilityData.unavailableItems.forEach((item) => {
          if (!item.itemAvailable) {
            item.options.forEach((opt) => {
              if (!opt.inStock) {
                const compositeKey = `${item.SpecialKey}|${norm(
                  opt.optionName,
                )}|${norm(opt.optionValue)}`;
                unavailableOptionsSet.add(compositeKey);
              }
            });
          }
        });

        setUnavailableOptions(unavailableOptionsSet);

        messageApi.error("Some product options are not available.", 8);

        setIsLoading(false);
        setLoadingText("");
        setOpen(true);
      }
    } catch (error) {
      console.error("Error sending quotation info:", error);

      messageApi.error(
        error.response?.data ||
          error?.message ||
          "Failed to send quotation info. Please try again.",
      );

      setTimeout(() => {
        setIsLoading(false);

        if (wasGuestModalOpen) {
          setOpenGuestInfoModal(true);
        }

        setOpen(true);
      }, 2000);
    }
  };

  //#region Cart Item Handlers
  const handleCartClear = () => {
    localStorage.removeItem("cartItems");
    setCartItems([]);

    messageApi.success("Cart cleared successfully!");

    setCartSpecialInstructions(""); // Clear the special instructions
  };

  const handleCartItemChangeQuantity = (item, change) => {
    const updatedCartItems = cartItems.map((cartItem) => {
      if (cartItem.specialKey === item.specialKey) {
        return {
          ...cartItem,
          quantity: Math.max(cartItem.quantity + change, 1), // Ensure quantity doesn't go below 1
        };
      }
      return cartItem;
    });

    setCartItems(updatedCartItems);

    localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));

    window.dispatchEvent(new Event("cartUpdated"));

    // messageApi.success(
    //   `Quantity for ${item.productName} updated to ${
    //     updatedCartItems.find(
    //       (cartItem) => cartItem.productId === item.productId
    //     ).quantity
    //   }`
    // );
  };

  const handleCartItemDelete = (item) => {
    const updatedCartItems = cartItems.filter(
      (cartItem) => cartItem.specialKey !== item.specialKey,
    );

    setCartItems(updatedCartItems);

    localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));

    messageApi.success(`${item.productName} removed from cart`);
  };
  //#endregion

  return (
    <>
      {contextHolder}

      <Spin
        tip={loadingText}
        spinning={isLoading}
        fullscreen
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      />

      <Drawer
        destroyOnClose={true}
        zIndex={999}
        title={
          <span style={{ fontSize: 18, fontWeight: "600", color: "#002753" }}>
            Your Cart
          </span>
        }
        extra={
          <CloseOutlined
            onClick={onClose}
            style={{ cursor: "pointer", color: "#002753", fontSize: 18 }}
          />
        }
        closable={false}
        onClose={onClose}
        open={open}
        footer={
          <Space
            direction="vertical"
            style={{ width: "100%", marginBottom: 10 }}
          >
            <p
              style={{
                color: "#002753",
                fontSize: 16,
                fontWeight: "500",
              }}
            >
              Additional Notes or Requirements
            </p>

            <Input.TextArea
              rows={2}
              value={cartSpecialInstructions}
              onChange={(e) => setCartSpecialInstructions(e.target.value)}
              placeholder="Enter any specific instructions or requests..."
              style={{
                width: "100%",
                borderRadius: 8,
                border: "1px solid #d9d9d9",
                fontSize: 14,
                padding: "8px 12px",
                backgroundColor: "#f5f5f5",
                resize: "none",
                minHeight: 80,
                maxHeight: 120,
                marginBottom: 10,
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                transition: "border-color 0.3s, box-shadow 0.3s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#002753";
                e.target.style.boxShadow = "0 2px 8px rgba(0, 38, 83, 0.2)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d9d9d9";
                e.target.style.boxShadow = "none";
              }}
            />

            <Divider style={{ margin: 0, marginBottom: 10 }} />

            <Popconfirm
              title="Are you sure you want to clear the cart?"
              onConfirm={handleCartClear}
              okText="Yes"
              cancelText="No"
              placement="top"
            >
              <Button
                block
                style={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #002753",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 16,
                  fontWeight: "500",
                }}
              >
                Clear All
              </Button>
            </Popconfirm>

            <Button
              block
              type="primary"
              style={{
                backgroundColor: cartItems.length === 0 ? "#f5f5f5" : "#002753",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 16,
                fontWeight: "500",
              }}
              onClick={handleRequestQuotation}
              disabled={isLoading || cartItems.length === 0}
            >
              Request Quotation
            </Button>
          </Space>
        }
        styles={{
          body: {
            paddingTop: 10,
          },
        }}
      >
        <List
          locale={{ emptyText: "Your cart is empty." }}
          dataSource={cartItems}
          renderItem={(item, index) => {
            return (
              <List.Item key={index}>
                <List.Item.Meta
                  avatar={
                    <Image
                      src={item.firstImage}
                      style={{ width: 80, height: 80, objectFit: "cover" }}
                    />
                  }
                  title={
                    <span
                      style={{
                        fontWeight: "600",
                        color: "#002753",
                        fontSize: 16,
                        marginBottom: 0,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        navigate(
                          getProductUrl(
                            item.productName,
                            item.sku,
                            item.productId,
                          ),
                        );

                        window.scrollTo(0, 0);

                        setIsLoading(true);

                        window.location.reload();
                      }}
                    >
                      {item.productName}
                    </span>
                  }
                  description={
                    <Flex vertical size={0} style={{ marginBottom: 0 }}>
                      <span
                        style={{
                          color: "#6B7280",
                        }}
                      >
                        SKU: {item.sku}
                      </span>

                      {Object.entries(item.options).map(([key, value]) => {
                        const compositeKey = `${item.specialKey}|${key
                          .trim()
                          .toLowerCase()}|${value.trim().toLowerCase()}`;

                        const isUnavailable =
                          unavailableOptions.has(compositeKey);

                        return (
                          <Flex
                            key={key}
                            style={{
                              fontSize: 12,
                              color: isUnavailable ? "#ff4d4f" : "#6B7280",
                              fontWeight: isUnavailable ? 600 : 400,
                            }}
                          >
                            {key}: {String(value)}
                          </Flex>
                        );
                      })}

                      <Flex
                        justify="space-between"
                        align="center"
                        style={{
                          color: "#6B7280",
                          fontSize: 14,
                        }}
                      >
                        <Flex justify="flex-start">
                          <Button
                            style={{
                              padding: "0 8px",
                              height: 24,
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleCartItemChangeQuantity(item, -1)
                            }
                          >
                            <span
                              style={{
                                color: "#002753",
                                fontWeight: "600",
                                marginTop: -2,
                              }}
                            >
                              -
                            </span>
                          </Button>

                          <span
                            style={{
                              margin: "0 8px",
                              padding: "0 12px",
                              height: 22,
                              display: "inline-block",
                              border: "1px solid #d9d9d9",
                              borderRadius: 6,
                              color: "#002753",
                            }}
                          >
                            {item.quantity}
                          </span>

                          <Button
                            style={{
                              padding: "0 8px",
                              height: 24,
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              handleCartItemChangeQuantity(item, 1)
                            }
                          >
                            <span
                              style={{
                                color: "#002753",
                                fontWeight: "600",
                                marginTop: -2,
                              }}
                            >
                              +
                            </span>
                          </Button>
                        </Flex>

                        <Flex
                          justify="flex-end"
                          style={{
                            color: "#002753",
                            fontWeight: "600",
                            marginRight: 20,
                          }}
                        >
                          <Button
                            type="text"
                            style={{
                              color: "#EF4444",
                              fontSize: 14,
                              padding: 0,
                              cursor: "pointer",
                            }}
                            onClick={() => handleCartItemDelete(item)}
                          >
                            <DeleteOutlined />
                          </Button>
                        </Flex>
                      </Flex>
                    </Flex>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Drawer>

      <Modal
        centered
        open={openGuestInfoModal}
        onCancel={() => {
          // form.resetFields();
          setOpenGuestInfoModal(false);
        }}
        onClose={() => {
          // form.resetFields();
          setOpenGuestInfoModal(false);
        }}
        destroyOnClose={true}
        footer={
          <Flex justify="end">
            <Button
              type="default"
              onClick={() => {
                // form.resetFields();
                setOpenGuestInfoModal(false);
              }}
              style={{
                marginRight: 8,
              }}
            >
              Close
            </Button>

            <Button
              type="primary"
              onClick={() => {
                form.validateFields().then((values) => {
                  // console.log("Guest Info:", values);

                  handleSendingQuotationInfo();
                });
              }}
              style={{
                color: "black",
              }}
              disabled={isLoading || cartItems.length === 0}
            >
              Submit
            </Button>
          </Flex>
        }
      >
        <p
          style={{
            fontWeight: "600",
            fontSize: 16,
            marginBottom: 16,
            color: "#002753",
          }}
        >
          Guest Information
        </p>

        <Form form={form}>
          <Form.Item
            label="Name"
            name="name"
            style={{
              marginBottom: 10,
            }}
            rules={[{ required: true, message: "" }]}
            hasFeedback
          >
            <Input placeholder="Enter your name" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            style={{
              marginBottom: 10,
            }}
            rules={[{ required: true, message: "" }]}
            hasFeedback
          >
            <Input placeholder="Enter your email" />
          </Form.Item>

          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[{ required: true, message: "" }]}
            hasFeedback
          >
            <PhoneInput
              autoFocus={false}
              country="MY"
              international
              withCountryCallingCode
              placeholder="+60"
              style={{
                width: "auto",
                borderRadius: 6,
                border: "1px solid #ffd600",
                padding: "7px 11px",
                fontSize: 14,
              }}
            />
          </Form.Item>
        </Form>

        <Divider />

        <p
          style={{
            fontSize: 12,
            color: "#6B7280",
            marginBottom: 0,
            fontStyle: "italic",
          }}
        >
          Have an account?{" "}
          <span
            style={{ color: "#002753" }}
            onClick={() => {
              navigate("/login");

              setOpenGuestInfoModal(false);
              setOpen(false);
            }}
          >
            Log in
          </span>
          . New here?{" "}
          <span
            style={{ color: "#002753" }}
            onClick={() => {
              navigate("/register");
              setOpenGuestInfoModal(false);
              setOpen(false);
            }}
          >
            Register
          </span>
        </p>
      </Modal>
    </>
  );
};

export default CartDrawer;
